<template>
  <div class="image-playground flex h-full bg-gray-50 dark:bg-gray-900">
    <div class="flex-1 flex flex-col">
      <!-- Header with Model Picker -->
      <div class="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        <ModelPicker
          :model-value="selectedModel"
          :models="models"
          :is-loading="isLoadingModels"
          @update:model-value="(v: string) => (selectedModel = v)"
        />
      </div>

      <!-- Error -->
      <Alert v-if="error" variant="error" class="m-4">{{ error }}</Alert>

      <!-- No Models Warning -->
      <Alert v-if="!isLoadingModels && models.length === 0" variant="warning" class="m-4">
        No image generation models available. Enable an image_generation model for this org.
      </Alert>

      <!-- Prompt + Settings -->
      <div class="p-4 space-y-3 border-b border-gray-200 dark:border-gray-700">
        <textarea
          v-model="prompt"
          rows="3"
          placeholder="Describe the image..."
          class="w-full p-2 border rounded-md text-sm bg-white dark:bg-gray-800 dark:border-gray-700"
        />

        <div class="flex flex-wrap gap-3 text-sm">
          <label class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">n</span>
            <input
              v-model.number="n"
              type="number"
              :min="nMin"
              :max="nMax"
              class="w-16 p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
            />
          </label>
          <label v-if="sizeOptions.length > 0" class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">size</span>
            <select
              v-model="size"
              class="p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              <option v-for="s in sizeOptions" :key="s" :value="s">{{ s }}</option>
            </select>
          </label>
          <label v-if="aspectRatioOptions.length > 0" class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">aspect ratio</span>
            <select
              v-model="aspectRatio"
              class="p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              <option v-for="a in aspectRatioOptions" :key="a" :value="a">{{ a }}</option>
            </select>
          </label>
          <label v-if="qualityOptions.length > 0" class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">quality</span>
            <select
              v-model="quality"
              class="p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              <option value="">auto</option>
              <option v-for="q in qualityOptions" :key="q" :value="q">{{ q }}</option>
            </select>
          </label>
          <label v-if="imageSizeOptions.length > 0" class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">image size</span>
            <select
              v-model="imageSize"
              class="p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              <option v-for="s in imageSizeOptions" :key="s" :value="s === 'auto' ? '' : s">
                {{ s }}
              </option>
            </select>
          </label>
          <label v-if="thinkingLevelOptions.length > 0" class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">thinking level</span>
            <select
              v-model="thinkingLevel"
              class="p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              <option value="">auto</option>
              <option v-for="t in thinkingLevelOptions" :key="t" :value="t">{{ t }}</option>
            </select>
          </label>
          <label v-if="outputMimeOptions.length > 0" class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">output format</span>
            <select
              v-model="outputMimeType"
              class="p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              <option value="">auto</option>
              <option v-for="m in outputMimeOptions" :key="m" :value="m">{{ m }}</option>
            </select>
          </label>
          <label class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">response_format</span>
            <select
              v-model="responseFormat"
              class="p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
            >
              <option value="b64_json">b64_json</option>
              <option value="url">url</option>
            </select>
          </label>
          <label class="flex flex-col">
            <span class="text-xs text-gray-500 mb-0.5">seed</span>
            <input
              :value="seed ?? ''"
              type="number"
              placeholder="random"
              class="w-24 p-1 border rounded bg-white dark:bg-gray-800 dark:border-gray-700"
              @input="(e: Event) => {
                const v = (e.target as HTMLInputElement).value;
                seed = v ? Number(v) : null;
              }"
            />
          </label>
        </div>

        <!-- Cost estimate -->
        <div class="text-xs text-gray-500 dark:text-gray-400">
          Est. cost: {{ estimateLabel }}
        </div>

        <div class="flex gap-2">
          <Button :disabled="!canSend || isLoading" @click="generate">
            <LoadingSpinner v-if="isLoading" size="sm" class="mr-1" />
            Generate
          </Button>
          <Button v-if="images.length > 0" variant="ghost" @click="clear">Clear</Button>
        </div>
      </div>

      <!-- API inspector -->
      <ApiInspector :request="lastRequest" :response="lastResponse" class="mx-4 mb-4" />

      <!-- Gallery -->
      <div class="flex-1 overflow-auto p-4 grid grid-cols-2 md:grid-cols-3 gap-4">
        <div
          v-for="(img, i) in images"
          :key="i"
          class="border rounded overflow-hidden bg-white dark:bg-gray-800 dark:border-gray-700"
        >
          <img :src="img" class="w-full h-auto" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { Alert, LoadingSpinner, Button } from '@asabdev/asab-ui';
  import ModelPicker from '../shared/ModelPicker.vue';
  import ApiInspector from '../shared/ApiInspector.vue';
  import { useAuthStore } from '@/stores/auth';
  import { createOpenAIClient } from '../../lib/openai-client';
  import type { Model } from '../../types/models';

  interface RequestInfo {
    method: string;
    url: string;
    headers?: Record<string, string>;
    body?: unknown;
  }
  interface ResponseInfo {
    status?: number;
    statusText?: string;
    headers?: Record<string, string>;
    body?: unknown;
    durationMs?: number;
  }
  const lastRequest = ref<RequestInfo | null>(null);
  const lastResponse = ref<ResponseInfo | null>(null);

  interface Props {
    models: Model[];
    isLoadingModels: boolean;
  }
  const props = defineProps<Props>();

  const authStore = useAuthStore();

  const selectedModel = ref('');
  const prompt = ref('');
  const n = ref(1);
  const size = ref('');
  const aspectRatio = ref('');
  const quality = ref('');
  const imageSize = ref('');
  const thinkingLevel = ref('');
  const outputMimeType = ref('');
  const responseFormat = ref<'b64_json' | 'url'>('b64_json');
  const seed = ref<number | null>(null);
  const images = ref<string[]>([]);
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  const canSend = computed(() => !!selectedModel.value && !!prompt.value && !!authStore.token);

  const currentModel = computed(() =>
    props.models.find(m => m.id === selectedModel.value)
  );

  // Metadata-driven UI options. Fall back to empty arrays so fields that
  // don't apply to this model (e.g. aspect ratio on gpt-image-1) stay hidden.
  const metaArr = (key: string): string[] => {
    const v = currentModel.value?.metadata?.[key];
    return Array.isArray(v) ? (v as string[]) : [];
  };
  const metaNum = (key: string, fallback: number): number => {
    const v = currentModel.value?.metadata?.[key];
    return typeof v === 'number' ? v : fallback;
  };
  const sizeOptions = computed(() => metaArr('sizes'));
  const aspectRatioOptions = computed(() => metaArr('aspect_ratios'));
  const qualityOptions = computed(() => metaArr('qualities'));
  const imageSizeOptions = computed(() => metaArr('image_sizes'));
  const thinkingLevelOptions = computed(() => metaArr('thinking_levels'));
  const outputMimeOptions = computed(() => metaArr('output_mime_types'));
  const nMin = computed(() => metaNum('n_min', 1));
  const nMax = computed(() => metaNum('n_max', 10));

  // When the selected model changes, snap defaults from metadata.
  watch(currentModel, (m: Model | undefined) => {
    if (!m) return;
    const meta = (m.metadata ?? {}) as Record<string, unknown>;
    const sizes = Array.isArray(meta.sizes) ? (meta.sizes as string[]) : [];
    const ratios = Array.isArray(meta.aspect_ratios) ? (meta.aspect_ratios as string[]) : [];
    const qualities = Array.isArray(meta.qualities) ? (meta.qualities as string[]) : [];
    size.value = sizes[0] ?? '';
    aspectRatio.value = ratios[0] ?? '';
    quality.value = qualities.includes(quality.value) ? quality.value : '';
    n.value = metaNum('n_default', 1);
    // Snap Gemini image config to first option (or blank = auto) when model changes.
    imageSize.value = '';
    thinkingLevel.value = '';
    outputMimeType.value = '';
  }, { immediate: true });

  const estimateLabel = computed(() => {
    const m = currentModel.value;
    const per = m?.pricing?.perImageCost;
    if (!per || !n.value) return '—';
    const credits = n.value * per * 10_000;
    return `${credits.toFixed(2)} credits (${n.value} × $${per.toFixed(3)})`;
  });

  async function generate() {
    error.value = null;
    isLoading.value = true;
    try {
      const client = createOpenAIClient({ token: authStore.token!, isApiKey: false });
      // Use raw fetch — SDK typing for images is strict and we need passthrough fields.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const c = client as any;
      const baseURL: string = c.baseURL || c._options?.baseURL;
      const body: Record<string, unknown> = {
        model: selectedModel.value,
        prompt: prompt.value,
        n: n.value,
        response_format: responseFormat.value,
      };
      if (size.value) body.size = size.value;
      if (aspectRatio.value) body.aspect_ratio = aspectRatio.value;
      if (quality.value) body.quality = quality.value;
      if (imageSize.value) body.image_size = imageSize.value;
      if (thinkingLevel.value) body.thinking_level = thinkingLevel.value;
      if (outputMimeType.value) body.output_mime_type = outputMimeType.value;
      if (seed.value !== null) body.seed = seed.value;
      const url = `${baseURL.replace(/\/$/, '')}/images/generations`;
      const started = performance.now();
      lastRequest.value = {
        method: 'POST',
        url,
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ***' },
        body,
      };
      lastResponse.value = null;
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${authStore.token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      const durationMs = Math.round(performance.now() - started);
      const resHeaders: Record<string, string> = {};
      res.headers.forEach((v, k) => (resHeaders[k] = v));
      if (!res.ok) {
        const t = await res.text();
        lastResponse.value = {
          status: res.status,
          statusText: res.statusText,
          headers: resHeaders,
          body: t,
          durationMs,
        };
        throw new Error(`HTTP ${res.status}: ${t}`);
      }
      const data = await res.json();
      lastResponse.value = {
        status: res.status,
        statusText: res.statusText,
        headers: resHeaders,
        body: data,
        durationMs,
      };
      images.value = (data.data || []).map((d: { b64_json?: string; url?: string }) =>
        d.b64_json ? `data:image/png;base64,${d.b64_json}` : d.url || ''
      );
    } catch (e) {
      error.value = (e as Error).message || 'Failed to generate image';
    } finally {
      isLoading.value = false;
    }
  }

  function clear() {
    images.value = [];
    error.value = null;
  }
</script>

<style scoped>
  .image-playground {
    min-height: 500px;
  }
</style>
